import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output } from "@angular/core";
import { HandoverStatus, HandoverStatusMetadata } from "@common/ADAPT.Common.Model/organisation/handover-status";
import { RoleBreezeModel } from "@common/ADAPT.Common.Model/organisation/role";
import { RoleConnection } from "@common/ADAPT.Common.Model/organisation/role-connection";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IAdaptMenuItem, MenuComponent } from "@common/ux/menu/menu.component";
import { DirectorySharedService } from "@org-common/lib/directory-shared/directory-shared.service";
import { EMPTY } from "rxjs";
import { switchMap, tap } from "rxjs/operators";
import { EditRoleConnectionDialogComponent } from "../edit-role-connection-dialog/edit-role-connection-dialog.component";

@Component({
    selector: "adapt-role-connection-actions",
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager,
    templateUrl: "./role-connection-actions.component.html",
})
export class RoleConnectionActionsComponent implements OnChanges {
    @Input({ required: true }) public roleConnection!: RoleConnection;
    @Input() public model = RoleBreezeModel;

    @Output() public roleConnectionChanged = new EventEmitter<RoleConnection>();

    public menuItems: IAdaptMenuItem[] = [];

    public constructor(
        private commonDataService: CommonDataService,
        private commonDialogService: AdaptCommonDialogService,
        private directorySharedService: DirectorySharedService,
    ) {
    }

    public async ngOnChanges() {
        await this.buildMenu();
    }

    private async buildMenu() {
        if (!this.roleConnection) {
            this.menuItems = [];
            return;
        }

        const menuItems: IAdaptMenuItem[] = [
            {
                text: "Edit role connection",
                icon: "fal fa-fw fa-edit",
                onClick: () => this.editRoleConnection().subscribe(),
                separatorBottom: true,
            },
        ];

        if (this.roleConnection.isNotHistoric()) {
            const setHandoverStatusMenuItems: IAdaptMenuItem[] = HandoverStatusMetadata.CurrentRoleHandoverOptions.map((handoverStatus) => ({
                text: handoverStatus.name,
                icon: handoverStatus.icon,
                // this can be null instead of undefined due to breeze, so just coalesce it to undefined
                isChecked: (this.roleConnection!.handoverStatus ?? undefined) === (handoverStatus.status ?? undefined),
                onIsCheckedChange: (isChecked) => this.changeHandoverStatus(isChecked ? handoverStatus.status : undefined),
            }));

            menuItems.push({
                text: "Set handover status...",
                icon: "fal fa-fw fa-hand-holding-box",
                items: setHandoverStatusMenuItems,
            });

            if (await this.directorySharedService.promiseToVerifyRoleConnectionCanEnd(this.roleConnection)) {
                menuItems.push({
                    text: "End role connection",
                    icon: "fal fa-fw fa-minus-square",
                    onClick: () => this.editRoleConnection(true).subscribe(),
                });
            }
        }

        if (this.roleConnection.entityAspect.entityState.isModified()) {
            menuItems.push({
                text: "Discard changes",
                icon: "fal fa-fw fa-trash",
                // only show the separator here if more items showing
                separatorTop: menuItems.length > 1,
                onClick: () => {
                    this.roleConnection.entityAspect.rejectChanges();
                    this.roleConnectionChanged.emit(this.roleConnection);
                },
            });
        } else if (this.roleConnection.entityAspect.entityState.isAdded()) {
            menuItems.push({
                text: "Remove new role connection",
                icon: "fal fa-fw fa-trash",
                onClick: () => {
                    // rejectChanges will remove it
                    this.roleConnection.entityAspect.rejectChanges();
                    this.roleConnectionChanged.emit(this.roleConnection);
                },
            });
        } else if (this.roleConnection.isHistoric()) {
            menuItems.push({
                text: "Delete role connection",
                icon: "fal fa-fw fa-trash",
                onClick: () => this.deleteRoleConnection().subscribe(),
            });
        }

        this.menuItems = [{
            icon: MenuComponent.SmallRootMenu.icon,
            padding: false,
            items: menuItems,
        }];
    }

    private editRoleConnection(endRoleConnection = false) {
        if (!this.roleConnection) {
            return EMPTY;
        }

        return this.commonDialogService.open(EditRoleConnectionDialogComponent, {
            roleConnection: this.roleConnection,
            model: this.model,
            endRoleConnection,
        }).pipe(
            tap(() => this.roleConnectionChanged.emit(this.roleConnection)),
        );
    }

    private changeHandoverStatus(newStatus?: HandoverStatus) {
        this.roleConnection.handoverStatus = newStatus ?? undefined;
        this.roleConnectionChanged.emit(this.roleConnection);
    }

    public deleteRoleConnection() {
        if (!this.roleConnection) {
            return EMPTY;
        }

        return this.directorySharedService.promptToRemoveRoleConnection(this.roleConnection).pipe(
            switchMap(() => this.commonDataService.remove(this.roleConnection)),
            tap(() => this.roleConnectionChanged.emit(this.roleConnection)),
        );
    }
}
